import { useRef, useState } from 'react';
import {
  Button, Checkbox, Spinner, Text, MessageBar, MessageBarBody, Badge,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  previewProtocolImport, commitProtocolImport, type ProtocolImportPreviewItem, type ProtocolImportResponse,
} from '../../api/protocol.api';
import { useT } from '../../i18n';

const useStyles = makeStyles({
  body:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, paddingTop: tokens.spacingVerticalS },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', alignItems: 'center' },
  scroll:  { maxHeight: '40vh', overflow: 'auto' },
  table:   { width: '100%', borderCollapse: 'collapse' },
  th:      { textAlign: 'left', padding: '6px 10px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  td:      { padding: '6px 10px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontSize: tokens.fontSizeBase200 },
});

interface Props { open: boolean; onClose: () => void }

/**
 * Importa marcos (fertilización / pulverización) desde Excel: valida (vista previa), muestra qué se
 * detectó y cuáles ya existen, y solo guarda sin errores. Un marco existente se omite salvo que se marque «Reemplazar».
 */
export function ProtocolImportDialog({ open, onClose }: Props) {
  const s = useStyles();
  const t = useT();
  const pi = t.protocol;
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ProtocolImportResponse | null>(null);
  const [done, setDone] = useState<ProtocolImportResponse | null>(null);
  const [replace, setReplace] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');

  const reset = () => {
    setFile(null); setPreview(null); setDone(null); setReplace(new Set()); setError('');
    if (fileRef.current) fileRef.current.value = '';
  };
  const close = () => { reset(); onClose(); };

  const previewMut = useMutation({
    mutationFn: (f: File) => previewProtocolImport(f),
    onSuccess: setPreview,
    onError: () => { setPreview(null); setError(pi.importReadError); },
  });
  const commitMut = useMutation({
    mutationFn: (f: File) => commitProtocolImport(f, [...replace]),
    onSuccess: (r) => { setDone(r); qc.invalidateQueries({ queryKey: ['protocols'] }); },
    onError: () => setError(pi.importError),
  });

  const onPick = (f: File | undefined) => {
    setPreview(null); setDone(null); setReplace(new Set()); setError('');
    setFile(f ?? null);
    if (f) previewMut.mutate(f);
  };

  const toggleReplace = (id: string, on: boolean) =>
    setReplace(prev => { const n = new Set(prev); if (on) n.add(id); else n.delete(id); return n; });

  const typeLabel = (ty: ProtocolImportPreviewItem['type']) =>
    ty === 'MARCO_FERTILIZACION' ? pi.typeMARCO_FERTILIZACION : pi.typeMARCO_PULVERIZACION;
  const detail = (it: ProtocolImportPreviewItem) =>
    it.type === 'MARCO_FERTILIZACION'
      ? `${it.productos ?? 0} ${pi.importProductos} × ${it.tiempos ?? 0} ${pi.importTiempos}`
      : `${it.aplicaciones ?? 0} ${pi.importAplicaciones}`;

  const errors = preview?.errors ?? [];
  const items = preview?.preview ?? [];
  const willWrite = items.filter(it => !it.exists || replace.has(it.protocolId)).length;
  const canConfirm = !!file && !!preview && errors.length === 0 && willWrite > 0 && !commitMut.isPending && !done;

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) close(); }}>
      <DialogSurface style={{ maxWidth: 820 }}>
        <DialogBody>
          <DialogTitle>{pi.importTitle}</DialogTitle>
          <DialogContent>
            <div className={s.body}>
              <Text size={200}>{pi.importIntro}</Text>
              <div className={s.actions}>
                <Button size="small" appearance="primary" onClick={() => fileRef.current?.click()} disabled={previewMut.isPending || commitMut.isPending}>
                  {pi.importChoose}
                </Button>
                <input
                  ref={fileRef} type="file" hidden
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => onPick(e.target.files?.[0])}
                />
                {file && <Text size={200}>{file.name}</Text>}
                {previewMut.isPending && <Spinner size="tiny" label={pi.importValidating} />}
              </div>

              {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}

              {preview && errors.length > 0 && (
                <>
                  <MessageBar intent="error"><MessageBarBody>{pi.importErrorsTitle} ({errors.length})</MessageBarBody></MessageBar>
                  <div className={s.scroll}>
                    <table className={s.table}>
                      <thead><tr>
                        <th className={s.th}>{pi.importColSheet}</th><th className={s.th}>{pi.importColMessage}</th>
                      </tr></thead>
                      <tbody>
                        {errors.map((e, i) => (
                          <tr key={i}><td className={s.td}>{e.sheet || '—'}</td><td className={s.td}>{e.message}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {preview && errors.length === 0 && !done && (
                <>
                  <Text size={200} weight="semibold">{items.length} {pi.importDetected}</Text>
                  <div className={s.scroll}>
                    <table className={s.table}>
                      <thead><tr>
                        <th className={s.th}>{pi.tableName}</th><th className={s.th}>{pi.tableType}</th>
                        <th className={s.th}>{pi.importColDetail}</th><th className={s.th}>{pi.importColStatus}</th>
                      </tr></thead>
                      <tbody>
                        {items.map(it => (
                          <tr key={it.protocolId}>
                            <td className={s.td}>{it.nombre}</td>
                            <td className={s.td}>{typeLabel(it.type)}</td>
                            <td className={s.td}>{detail(it)}</td>
                            <td className={s.td}>
                              {it.exists ? (
                                <Checkbox
                                  checked={replace.has(it.protocolId)}
                                  onChange={(_, d) => toggleReplace(it.protocolId, !!d.checked)}
                                  label={pi.importReplace}
                                />
                              ) : (
                                <Badge appearance="tint" color="success">{pi.importStatusNew}</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {items.some(it => it.exists) && <Text size={200}>{pi.importExistsHint}</Text>}
                  {items.some(it => it.type === 'MARCO_FERTILIZACION') && (
                    <MessageBar intent="info"><MessageBarBody>{pi.importUnitNote}</MessageBarBody></MessageBar>
                  )}
                </>
              )}

              {done && (
                <MessageBar intent="success">
                  <MessageBarBody>
                    {pi.importDone}: {done.summary.created} {pi.importCreated}
                    {done.summary.replaced ? ` · ${done.summary.replaced} ${pi.importReplaced}` : ''}
                    {done.summary.skipped ? ` · ${done.summary.skipped} ${pi.importSkipped}` : ''}
                  </MessageBarBody>
                </MessageBar>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={close}>{pi.importClose}</Button>
            <Button
              appearance="primary" disabled={!canConfirm}
              icon={commitMut.isPending ? <Spinner size="tiny" /> : undefined}
              onClick={() => file && commitMut.mutate(file)}
            >
              {commitMut.isPending ? pi.importImporting : pi.importConfirm}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
